/**
 * Pestaña de gestión de tokens de eliminación (solo super admin).
 * Muestra el token activo, countdown a la próxima rotación, botón para
 * generar uno nuevo anticipadamente, historial de tokens y uso por token.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from 'react-redux-firebase';
import { Card, Statistic, Button, Table, Tag, Typography, notification, Modal, Drawer, List } from 'antd';
import { SafetyOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

// Formatea ms restantes a "Xd Yh Zm".
const fmtCountdown = (ms) => {
  if (ms <= 0) return 'Renovación pendiente…';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${d}d ${h}h ${m}m`;
};

const tsToMs = (t) => (t?.toMillis ? t.toMillis() : (typeof t === 'number' ? t : null));
const tsToStr = (t) => {
  const ms = tsToMs(t);
  return ms ? new Date(ms).toLocaleString('es-CO') : '—';
};

export const Tokens = () => {
  const firebase = useFirebase();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [usageOpen, setUsageOpen] = useState(false);
  const [usages, setUsages] = useState([]);
  const [usageToken, setUsageToken] = useState(null);

  // Tick del countdown cada 30s.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const db = firebase.firestore();
      const snap = await db.collection('tokens').orderBy('createdAt', 'desc').get();
      setList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      notification.error({ message: 'No se pudo cargar los tokens', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [firebase]);

  useEffect(() => { load(); }, [load]);

  const active = list.find(t => t.active);

  const generateNow = () => {
    Modal.confirm({
      title: 'Generar un token nuevo ahora',
      content: 'El token actual quedará inválido de inmediato. ¿Continuar?',
      okText: 'Generar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setGenerating(true);
        try {
          await firebase.functions().httpsCallable('generateDeletionTokenNow')({});
          notification.success({ message: 'Token nuevo generado' });
          await load();
        } catch (e) {
          notification.error({ message: 'No se pudo generar el token', description: e?.message });
        } finally {
          setGenerating(false);
        }
      }
    });
  };

  const openUsages = async (tok) => {
    setUsageToken(tok);
    setUsageOpen(true);
    setUsages([]);
    try {
      const db = firebase.firestore();
      const snap = await db.collection('tokens').doc(tok.id).collection('usages').orderBy('usedAt', 'desc').get();
      setUsages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setUsages([]);
      notification.error({ message: 'No se pudo cargar el uso del token', description: e?.message });
    }
  };

  const columns = [
    { title: 'Periodo', dataIndex: 'period', key: 'period' },
    { title: 'Token', dataIndex: 'secret', key: 'secret', render: v => <Text code>{v}</Text> },
    { title: 'Estado', dataIndex: 'active', key: 'active',
      render: a => a ? <Tag color="green">Activo</Tag> : <Tag>Vencido</Tag> },
    { title: 'Creado', dataIndex: 'createdAt', key: 'createdAt', render: tsToStr },
    { title: 'Creado por', dataIndex: 'createdBy', key: 'createdBy',
      render: v => v === 'system' ? 'Automático' : v },
    { title: 'Usos', dataIndex: 'useCount', key: 'useCount', render: v => v || 0 },
    { title: '', key: 'acc', render: (_, r) => <Button size="small" onClick={() => openUsages(r)}>Ver uso</Button> },
  ];

  const expiresMs = tsToMs(active?.expiresAt);

  return (
    <div>
      <Title level={4}><SafetyOutlined /> Tokens de eliminación</Title>

      <Card style={{ marginBottom: 16 }} loading={loading}>
        {active ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center' }}>
            <div>
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>Token activo</div>
              <Text strong copyable style={{ fontFamily: 'monospace', fontSize: 24 }}>{active.secret}</Text>
            </div>
            <Statistic title="Periodo" value={active.period} />
            <Statistic title="Próxima rotación en" value={fmtCountdown(expiresMs != null ? expiresMs - now : -1)} />
            <Button type="primary" icon={<ReloadOutlined />} loading={generating} onClick={generateNow}>
              Generar nuevo ahora
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Text type="warning">No hay un token activo todavía.</Text>
            <Button type="primary" icon={<ReloadOutlined />} loading={generating} onClick={generateNow}>
              Generar el primer token
            </Button>
          </div>
        )}
      </Card>

      <Table rowKey="id" loading={loading} columns={columns} dataSource={list} size="small" />

      <Drawer
        title={usageToken ? `Uso del token ${usageToken.period} (${usageToken.secret})` : 'Uso del token'}
        open={usageOpen}
        onClose={() => setUsageOpen(false)}
        width={520}
      >
        <List
          dataSource={usages}
          locale={{ emptyText: 'Sin usos registrados' }}
          renderItem={u => (
            <List.Item key={u.id}>
              <List.Item.Meta
                title={`${u.userName || u.uid} — ${tsToStr(u.usedAt)}`}
                description={`${u.itemType === 'file' ? 'Archivo' : 'Carpeta'}: ${u.itemName || '—'}${u.customerName ? ` · Cliente: ${u.customerName}` : ''}`}
              />
            </List.Item>
          )}
        />
      </Drawer>
    </div>
  );
};
